/**
 * Account Configuration catalog (platform-owner control).
 *
 * Agency Type → Current Plan. Plan metadata drives derived product/portal/workspace.
 * Prefers the catalog from GET /admin/agencies/:id/classification; falls back to
 * this mirror of backend/services/account_classification_catalog.py.
 */

export const AGENCY_TYPE_OPTIONS = [
  { value: 'credit_repair', label: 'Credit Repair Agency' },
  { value: 'funding', label: 'Funding Agency' },
  { value: 'self_repair', label: 'Self-Repair' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'internal', label: 'Internal / Platform Managed' },
];

export const PRODUCT_TYPE_OPTIONS = [
  { value: 'credit_repair', label: 'Credit Repair' },
  { value: 'funding_hq', label: 'FundGenius HQ' },
  { value: 'self_repair', label: 'Self Repair' },
  { value: 'multi_product', label: 'Multi-Product' },
];

export const DEFAULT_PORTAL_OPTIONS = [
  { value: 'credit_repair', label: 'Credit Repair' },
  { value: 'fundgenius', label: 'FundGenius HQ' },
  { value: 'self_repair', label: 'Self Repair' },
  { value: 'platform', label: 'Platform Console' },
];

export const ACCOUNT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const WORKSPACE_MODE_OPTIONS = [
  { value: 'agency_owner', label: 'Agency Owner Workspace' },
  { value: 'funding_workspace', label: 'Funding Workspace' },
  { value: 'credit_repair_workspace', label: 'Credit Repair Workspace' },
  { value: 'self_repair_workspace', label: 'Self-Repair Workspace' },
];

export const CLASSIFICATION_BADGE_BY_TYPE = {
  credit_repair: { label: 'Credit Repair', color: 'neutral' },
  funding: { label: 'FundGenius', color: 'cyan' },
  self_repair: { label: 'Self-Repair', color: 'green' },
  hybrid: { label: 'Hybrid', color: 'purple' },
  internal: { label: 'Internal', color: 'slate' },
};

export const CLASSIFICATION_PLANS = [
  {
    planId: 'auto_starter',
    name: 'Auto Starter',
    price: 49,
    group: 'Self-Repair',
    compatibleAgencyTypes: ['self_repair'],
    defaults: {
      agency_type: 'self_repair',
      product_type: 'self_repair',
      default_portal: 'self_repair',
      workspace_mode: 'self_repair_workspace',
    },
  },
  {
    planId: 'repair_pro',
    name: 'Repair Pro',
    price: 99,
    group: 'Professional Credit Repair',
    compatibleAgencyTypes: ['credit_repair', 'hybrid'],
    defaults: {
      agency_type: 'credit_repair',
      product_type: 'credit_repair',
      default_portal: 'credit_repair',
      workspace_mode: 'credit_repair_workspace',
    },
  },
  {
    planId: 'agency_repair',
    name: 'Agency Repair',
    price: 299,
    group: 'Professional Credit Repair',
    compatibleAgencyTypes: ['credit_repair', 'hybrid'],
    defaults: {
      agency_type: 'credit_repair',
      product_type: 'credit_repair',
      default_portal: 'credit_repair',
      workspace_mode: 'credit_repair_workspace',
    },
  },
  {
    planId: 'agency_pro_repair',
    name: 'Agency Pro Repair',
    price: 599,
    group: 'Professional Credit Repair',
    compatibleAgencyTypes: ['credit_repair', 'hybrid'],
    defaults: {
      agency_type: 'credit_repair',
      product_type: 'credit_repair',
      default_portal: 'credit_repair',
      workspace_mode: 'credit_repair_workspace',
    },
  },
  {
    planId: 'fundgenius_starter',
    name: 'FundGenius Starter',
    price: 197,
    group: 'FundGenius',
    compatibleAgencyTypes: ['funding', 'hybrid'],
    defaults: {
      agency_type: 'funding',
      product_type: 'funding_hq',
      default_portal: 'fundgenius',
      workspace_mode: 'funding_workspace',
    },
  },
  {
    planId: 'fundgenius_growth',
    name: 'FundGenius Growth',
    price: 497,
    group: 'FundGenius',
    compatibleAgencyTypes: ['funding', 'hybrid'],
    defaults: {
      agency_type: 'funding',
      product_type: 'funding_hq',
      default_portal: 'fundgenius',
      workspace_mode: 'funding_workspace',
    },
  },
  {
    planId: 'internal_override',
    name: 'Internal Administrative Override',
    price: 0,
    group: 'Administrative',
    compatibleAgencyTypes: ['internal'],
    defaults: {
      agency_type: 'internal',
      product_type: null,
      default_portal: 'platform',
      workspace_mode: 'agency_owner',
    },
  },
];

export const PLAN_GROUP_ORDER = [
  'Self-Repair',
  'Professional Credit Repair',
  'FundGenius',
  'Administrative',
];

export const NOT_CONFIGURED = 'Not configured';

const optionLabel = (options, value) => {
  if (value === null || value === undefined || value === '') return NOT_CONFIGURED;
  const found = options.find((o) => o.value === value);
  return found ? found.label : value;
};

export const agencyTypeLabel = (v) => optionLabel(AGENCY_TYPE_OPTIONS, v);
export const productTypeLabel = (v) => optionLabel(PRODUCT_TYPE_OPTIONS, v);
export const defaultPortalLabel = (v) => optionLabel(DEFAULT_PORTAL_OPTIONS, v);
export const accountStatusLabel = (v) => optionLabel(ACCOUNT_STATUS_OPTIONS, v);
export const workspaceModeLabel = (v) => optionLabel(WORKSPACE_MODE_OPTIONS, v);

export const findPlan = (planId, plans = CLASSIFICATION_PLANS) =>
  plans.find((p) => (p.planId || p.plan_id) === planId) || null;

export const planLabel = (planId, plans = CLASSIFICATION_PLANS) => {
  const plan = findPlan(planId, plans);
  if (!plan) return NOT_CONFIGURED;
  return `${plan.name} — $${plan.price}/mo`;
};

export const normalizePlan = (plan) => {
  if (!plan) return null;
  const defaults = plan.defaults || {};
  return {
    planId: plan.planId || plan.plan_id,
    name: plan.name,
    price: plan.price_monthly ?? plan.price,
    group: plan.group,
    compatibleAgencyTypes:
      plan.compatibleAgencyTypes
      || plan.compatible_agency_types
      || (defaults.agency_type ? [defaults.agency_type] : []),
    primaryProduct: plan.primary_product ?? defaults.product_type ?? null,
    defaultPortal: plan.default_portal ?? defaults.default_portal ?? null,
    workspaceMode: plan.workspace_mode ?? defaults.workspace_mode ?? null,
    classification: plan.classification || null,
    defaults,
  };
};

export const isPlanCompatibleWithAgencyType = (plan, agencyType) => {
  if (!plan || !agencyType) return false;
  const normalized = normalizePlan(plan);
  return (normalized.compatibleAgencyTypes || []).includes(agencyType);
};

export const filterPlansForAgencyType = (agencyType, plans = CLASSIFICATION_PLANS) => {
  if (!agencyType) return [];
  return plans.map(normalizePlan).filter((p) => isPlanCompatibleWithAgencyType(p, agencyType));
};

export const groupedPlans = (plans = CLASSIFICATION_PLANS) => {
  const normalized = plans.map(normalizePlan);
  return PLAN_GROUP_ORDER.map((group) => ({
    group,
    plans: normalized.filter((p) => p.group === group),
  })).filter((g) => g.plans.length > 0);
};

export const getClassificationBadge = (agencyOrType) => {
  if (!agencyOrType) return null;
  if (typeof agencyOrType === 'object') {
    if (agencyOrType.classification?.label) {
      return {
        label: agencyOrType.classification.label,
        color: agencyOrType.classification.color || 'neutral',
      };
    }
    const type =
      agencyOrType.agency_type
      || agencyOrType.agencyType
      || null;
    if (!type) return null;
    return CLASSIFICATION_BADGE_BY_TYPE[type] || null;
  }
  return CLASSIFICATION_BADGE_BY_TYPE[String(agencyOrType).trim().toLowerCase()] || null;
};
